import React, { useEffect, useMemo, useRef, useState } from "react";

import {
  Box,
  Button,
  Container,
  Stack,
  Typography,
  alpha,
} from "@mui/material";

import { Link as RouterLink } from "react-router-dom";
import ArrowLeft2 from "iconsax-reactjs/dist/esm/ArrowLeft2.js";

import { useGetServicesQuery } from "../../../store/Slice/rtk";

import CategoryCard from "./CategoryCard";
import CategorySkeletonGrid from "./CategorySkeletonGrid";

const FILE_BASE_URL = import.meta.env.VITE_FILE_API;

function buildImageUrl(imageUrl) {
  if (!imageUrl) return "";

  if (imageUrl.startsWith("http")) {
    return imageUrl;
  }

  return `${FILE_BASE_URL}${imageUrl}`;
}

function useInViewOnce(threshold = 0.15) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const node = ref.current;

    if (!node) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      {
        threshold,
      },
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, [threshold]);

  return [ref, inView];
}

export default function CategorySection({
  categoryData,
  categoryLoading,
  categoryError,
  categoryFetching,
}) {
  const [sectionRef, inView] = useInViewOnce();

  const categories = useMemo(() => {
    const raw = categoryData?.data ?? [];

    return raw
      .filter((item) => item.isActive)
      .map((item) => ({
        id: item._id,
        title: item.title,
        slug: item.slug,
        image: item.imageUrl ? buildImageUrl(item.imageUrl) : null,
        icon: item.iconUrl || null,
      }));
  }, [categoryData]);

  if (categoryError) {
    return null;
  }

  const loading = categoryLoading || categoryFetching;

  return (
    <Box
      ref={sectionRef}
      sx={{
        direction: "rtl",
        bgcolor: "background.default",
        py: {
          xs: 6,
          md: "72px",
        },
        overflow: "hidden",
      }}
    >
      <Container maxWidth="lg">
        {/* Header */}
        <Stack
          spacing={1.25}
          alignItems="center"
          textAlign="center"
          sx={{
            mb: {
              xs: 4,
              md: 5.5,
            },
          }}
        >
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,

              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),

              color: "primary.main",

              fontFamily: "Dana",
              fontSize: 13.5,
              fontWeight: 700,

              px: 2,
              py: 0.75,

              borderRadius: 999,
            }}
          >
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                bgcolor: "primary.main",
              }}
            />
            دسته‌بندی خدمات
          </Box>

          <Typography
            component="h2"
            sx={{
              fontFamily: "YekanPlus",
              fontWeight: 800,
              fontSize: {
                xs: 24,
                sm: 30,
                md: 36,
              },
              color: "primary.main",
            }}
          >
            هر خدمتی که لازم داری،{" "}
            <Box
              component="span"
              sx={{
                color: "secondary.main",
              }}
            >
              یک کلیک تا اینجا
            </Box>
          </Typography>

          <Typography
            sx={{
              fontFamily: "Dana",
              fontSize: 15.5,
              color: "text.secondary",
              maxWidth: 520,
            }}
          >
            از تعمیرات ساختمان تا نظافت و سلامت — دسته‌ی مورد نظرت رو انتخاب کن
            تا بهترین متخصص‌ها رو ببینی.
          </Typography>
        </Stack>

        {/* Categories */}
        {loading ? (
          <CategorySkeletonGrid />
        ) : (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              alignItems: "stretch",
              gap: {
                xs: 1.5,
                sm: 2,
              },
            }}
          >
            {categories.map((item, index) => (
              <Box
                key={item.id}
                sx={{
                  width: {
                    xs: "calc((100% - 24px) / 3)",
                    sm: "calc((100% - 32px) / 4)",
                    md: "calc((100% - 80px) / 6)",
                  },
                  display: "flex",
                  alignItems: "stretch",
                  minWidth: 0,
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    "& > *": {
                      width: "100%",
                      flex: 1,
                    },
                  }}
                >
                  <CategoryCard item={item} index={index} inView={inView} />
                </Box>
              </Box>
            ))}
          </Box>
        )}

        {/* All categories */}
        <Stack
          alignItems="center"
          sx={{
            mt: {
              xs: 4,
              md: 5,
            },
          }}
        >
          <Button
            component={RouterLink}
            to="/category?level=0"
            variant="outlined"
            endIcon={<ArrowLeft2 size={18} variant="Bold" />}
            sx={{
              fontFamily: "Dana",
              fontWeight: 700,
              fontSize: 14,

              px: 3.5,
              py: 1.1,

              borderRadius: 999,

              borderWidth: 1.5,
              borderColor: "primary.main",

              color: "primary.main",

              "&:hover": {
                borderWidth: 1.5,
                bgcolor: "primary.main",
                color: "#fff",
              },
            }}
          >
            مشاهده همه دسته‌بندی‌ها
          </Button>
        </Stack>
      </Container>
    </Box>
  );
}
